import { Chip } from '@mui/material';
import Link from 'components/Link';
import { IColumn } from 'kosmos/components/ResourceTable';
import { ISchema } from 'kosmos/dialogs/CreateResourceModal';
import { AuditLogDto, AuditLogSummaryDto, CompanyListDto, OrganisationDto, OrganisationScoredCompaniesDto, UserDto } from 'models/dtoTypes';
import { date2str } from 'utils/date';
import { getKosmosReportLink } from 'utils/report';

// 1. details tab

// 2. users tab
export const userCreateSchema: ISchema = {
  fields: [
    {
      label: 'First Name',
      name: 'firstName',
      widget: 'text',
      required: true,
      description: ''
    },
    {
      label: 'Last Name',
      name: 'lastName',
      widget: 'text',
      required: true,
      description: '',
    },
    {
      label: 'Email',
      name: 'email',
      widget: 'text',
      required: true,
      description: '',
    },
    {
      label: 'Role',
      name: 'userRole',
      widget: 'select',
      required: true,
      placeholder: 'Select the user role',
      description: 'User role under this organisation, "TFP Service API" will require extra setup and its role cannot be updated later',
      options: [
        {
          alias: 'sme',
          label: 'Business',
        },
        {
          alias: 'tfp',
          label: 'TFP User',
        },
        {
          alias: 'serviceTfp',
          label: 'TFP Service API',
        },
        {
          alias: 'orgAdmin',
          label: 'Organisation Admin',
        }
      ],
    }
  ]
};

const userRoleNameMapper = {
  'tfp': 'TFP User',
  'servicetfp': 'TFP Service API',
  'orgadmin': 'Organisation Admin',
  'admin': 'Global Admin',
  'sme': 'Business'
};

export const generateUserColumns = (
  organisation: OrganisationDto,
  options: {
    isAdmin: boolean,
  },
  actions: {
    handleEnableUser: (user: UserDto) => Promise<UserDto | undefined>,
    handleDisableUser: (user: UserDto) => Promise<UserDto | undefined>,
  }
): IColumn<UserDto>[] => {
  const isActive = organisation.status === 'Active';
  const { isAdmin } = options;
  const { handleEnableUser, handleDisableUser } = actions;
  return [
    {
      label: 'Email',
      dataIndex: 'email',
      sortIndex: 'email',
    },
    {
      label: 'First Name',
      dataIndex: 'firstName',
    },
    {
      label: 'Last Name',
      dataIndex: 'lastName',
    },
    {
      label: 'Role*',
      dataIndex: 'userRole',
      render: (user) => {
        const role = user.userRole.toString().toLowerCase();
        return <span>{userRoleNameMapper[role] || role}</span>;
      }
    },
    {
      label: 'Active Directory ID',
      dataIndex: 'aadUserId',
    },
    {
      label: 'Account Enabled',
      render: (user) => {
        const enabled = Boolean(user.accountEnabled);
        return <Chip label={enabled ? 'Yes' : 'No'} color={enabled ? 'primary' : 'default'} />;
      }
    },
    ...(isAdmin && [
      {
        label: 'Action',
        actions: [
          {
            label: 'Enable',
            hide: (user: UserDto) => !isActive || !user.aadUserId || user.accountEnabled,
            onClick: handleEnableUser
          },
          {
            label: 'Disable',
            hide: (user: UserDto) => !isActive || !user.aadUserId || !user.accountEnabled,
            onClick: handleDisableUser
          }
        ],
      },
    ] || []),
  ];
};

// company list tab
export const companyListCreateSchema: ISchema = {
  fields: [
    {
      label: 'List Name',
      name: 'name',
      required: true,
      widget: 'text',
      description: ''
    },
    {
      label: 'Description',
      name: 'description',
      required: true,
      widget: 'text',
    },
  ]
};

export const generateCompanyListColumns = (): IColumn<CompanyListDto>[] => {
  return [
    {
      label: 'Name',
      sortIndex: 'name',
      render: (model: CompanyListDto) => {
        return (
          <Link to={`/companyLists/${model?.id || ''}`} text={model?.name || ''}/>
        );
      }
    },
    {
      label: 'Description',
      dataIndex: 'description',
    },
    {
      label: 'Company Count',
      dataIndex: 'companyCount',
    },
  ];
};

// 3. activities tab
export const generateActivityColumns = (): IColumn<AuditLogDto>[] => {
  return [
    {
      label: 'User',
      render: (auditLog) => {
        const user = auditLog.user;
        return <>{user?.email || auditLog.aadUserId} </>;
      }
    },
    {
      label: 'Action Name',
      dataIndex: 'actionName',
    },
    {
      label: 'Controller',
      dataIndex: 'controllerName',
    },
    {
      label: 'Created',
      dataIndex: 'created',
      sortIndex: 'created',
      render: (auditLog) => {
        return date2str(auditLog.created, 'DD MMM YYYY, HH:mm:ss');
      }
    },
    {
      label: 'Meta',
      dataIndex: 'metadata',
    },
  ];
};

// 4. audit tab
export const auditLogFilterSchema = [
  {
    label: 'Action Name',
    name: 'actionName',
    widget: 'text',
  },
  {
    label: 'User',
    name: 'aadUserId',
    widget: 'userSelect',
  },
  {
    label: 'Period',
    name: 'createdMonth',
    widget: 'monthPicker',
  },
];

export const generateAuditLogSummaryColumns = (): IColumn<AuditLogSummaryDto>[] => {
  return [
    {
      label: 'Action Name',
      dataIndex: 'actionName',
    },
    {
      label: 'Event Name',
      dataIndex: 'eventName',
    },
    {
      label: 'Count',
      dataIndex: 'count',
    },
  ];
};

// 5. scored company tab
export const scoredCompaniesFilterSchema = [
  {
    label: 'Name',
    name: 'name',
    widget: 'text',
  },
  {
    label: 'Scored date from',
    name: 'lastScoredAtStart',
    widget: 'datePicker',
  },
  {
    label: 'Scored date to',
    name: 'lastScoredAtEnd',
    widget: 'datePicker',
  },
];

export const generateScoredCompaniesColumns = (organisationId?: string): IColumn<OrganisationScoredCompaniesDto>[] => {
  return [
    {
      label: 'Name',
      dataIndex: 'name',
      render: (company) => {
        const companyId: string = company.companyId || '';
        return <Link to={getKosmosReportLink(companyId, organisationId)} text={company.companyName || 'Unknown'}/>;
      },
      sortIndex: 'name',
    },

    {
      label: 'Country',
      dataIndex: 'companyCountry',
    },
    {
      label: 'Score',
      dataIndex: 'score',
    },
    {
      label: 'Scoring State',
      dataIndex: 'scoringState',
    },
    {
      label: 'Scored At',
      render: (company) => {
        const scoredAt: string = company.scoredAt || '';
        return date2str(scoredAt);
      },
      sortIndex: 'scoredAt',
    },
  ];
};

